import { TruckIcon } from 'lucide-react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import {
  DataTable,
  EmptyState,
  ErrorNotice,
  Pagination,
  PageHeading,
  Panel,
  Pill,
  SearchInput,
  SegmentedControl,
  SkeletonTable,
} from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import { useTableSort } from '@/lib/use-table-sort';
import { APP_FEATURE, useAppFeatures } from '@/lib/app-features';
import { useUrlPage, useUrlSearch } from '@/lib/use-list-query-state';

/** Sortable columns — root shipment fields only (Event is a populated ref). */
const SHIPMENT_SORT_KEYS = ['carrier', 'status', 'eta'];

/** Newest arrival dates first, so in-flight shipments lead the list. */
const DEFAULT_SHIPMENT_SORT = { key: 'eta', direction: 'desc' } as const;
import {
  RETURN_LABEL_VIEW,
  SHIPMENT_STATUSES,
  carrierLabel,
  listAllShipments,
  shipmentStatusLabel,
  type Shipment,
  type ShipmentStatus,
} from '@/lib/events';
import { SHIPMENT_TONES } from './event-tabs';

/** The filter segment for return labels Production should chase; not a tracking status. */
const UNUSED_RETURN_LABELS = 'unused-return-labels';
type ShipmentFilter = ShipmentStatus | 'all' | typeof UNUSED_RETURN_LABELS;

/** The URL param that narrows the list to return labels. */
const RETURN_LABELS_PARAM = 'returnLabels';

/**
 * Reads the shipment's tracking status, however V3 shaped it.
 *
 * @param shipment - The shipment.
 * @returns The status token, uppercased.
 */
function shipmentStatus(shipment: Shipment): string {
  return shipment.trackingStatus?.status ?? shipment.status ?? 'UNKNOWN';
}

/** Names the linked event, whatever shape `relatedTo` came back as. */
function relatedEvent(shipment: Shipment): { id?: string; label: string; program?: string } {
  const related = shipment.relatedTo;
  if (typeof related === 'object' && related !== null) {
    // Contract + company lead — same identity staff use everywhere else;
    // the program name rides underneath.
    const label =
      [related.contractNumber, related.account?.name].filter(Boolean).join(' · ') || 'Event';
    const program = related.programDisplayName ?? related.programName;
    return { id: related._id, label, ...(program ? { program } : {}) };
  }
  return { label: '—' };
}

/**
 * Every shipment in flight, across all events — a paginated list narrowed by
 * carrier or tracking number.
 *
 * @returns The shipments page.
 */
export function ShipmentsPage(): React.ReactElement {
  const [params, setParams] = useSearchParams();
  const { search, setSearch, debounced } = useUrlSearch();
  const { page, setPage } = useUrlPage();

  // Soonest-arriving first is what operations actually scan for; shipments
  // with no ETA (untracked carriers) fall to the end.
  const { sort, onSort } = useTableSort(SHIPMENT_SORT_KEYS, DEFAULT_SHIPMENT_SORT);

  const returnLabelsOn = useAppFeatures()(APP_FEATURE.returnLabels);
  const statusParam = params.get('status');
  const statusFilter =
    statusParam !== null && statusParam in SHIPMENT_STATUSES
      ? (statusParam as ShipmentStatus)
      : undefined;
  const unusedOnly = returnLabelsOn && params.get(RETURN_LABELS_PARAM) === RETURN_LABEL_VIEW.unused;

  const setFilter = (next: ShipmentFilter): void => {
    setParams(
      (previous) => {
        const nextParams = new URLSearchParams(previous);
        nextParams.delete('status');
        nextParams.delete(RETURN_LABELS_PARAM);
        if (next === UNUSED_RETURN_LABELS) nextParams.set(RETURN_LABELS_PARAM, RETURN_LABEL_VIEW.unused);
        else if (next !== 'all') nextParams.set('status', next);
        nextParams.delete('page');
        return nextParams;
      },
      { replace: true },
    );
  };

  const shipments = useQuery({
    queryKey: ['shipments', debounced, statusFilter, unusedOnly, page, sort],
    queryFn: ({ signal }) =>
      listAllShipments(
        {
          page,
          ...(debounced ? { search: debounced } : {}),
          ...(unusedOnly ? { returnLabels: RETURN_LABEL_VIEW.unused } : statusFilter ? { status: statusFilter } : {}),
          ...(sort ? { sort } : {}),
        },
        signal,
      ),
    placeholderData: keepPreviousData,
  });

  const rangeStart = shipments.data ? (shipments.data.page - 1) * 25 + 1 : 0;
  const rangeEnd = shipments.data ? rangeStart + shipments.data.shipments.length - 1 : 0;

  return (
    <section className="animate-portal-rise">
      <PageHeading
        eyebrow="OPERATIONS"
        title="Shipments"
        subtitle="Everything moving to and from events, with live tracking state."
      />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <SearchInput
          data-guide="shipments-page-search"
          label="Search shipments"
          placeholder="Carrier or tracking #"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          className="w-full max-w-sm"
        />
        {/* Scrolls rather than wrapping: six segments overflow a narrow screen. */}
        <div className="-mx-1 max-w-full overflow-x-auto px-1 py-0.5">
          <SegmentedControl<ShipmentFilter> guide="shipments-page-filter"
            label="Which shipments to show"
            options={[
              { key: 'all', label: 'All' },
              ...Object.entries(SHIPMENT_STATUSES).map(([key, label]) => ({
                key: key as ShipmentStatus,
                label,
              })),
              ...(returnLabelsOn ? [{ key: UNUSED_RETURN_LABELS, label: 'Unused return labels', shortLabel: 'Unused labels' } as const] : []),
            ]}
            value={unusedOnly ? UNUSED_RETURN_LABELS : (statusFilter ?? 'all')}
            onChange={setFilter}
          />
        </div>
      </div>

      {shipments.isError ? (
        <ErrorNotice title="Shipments unavailable" message={errorMessage(shipments.error)} />
      ) : shipments.isPending ? (
        <SkeletonTable columns={['w-[26%]', 'w-[24%]', 'w-[18%]', 'w-[12%]']} label="Loading shipments…" />
      ) : shipments.data.shipments.length === 0 ? (
        <Panel>
          {debounced ? (
            <EmptyState message={`No shipments match “${debounced}”.`} />
          ) : unusedOnly ? (
            <EmptyState
              icon={<TruckIcon aria-hidden className="size-6" />}
              title="No unused return labels"
              message="Every return label from an event more than a week ago has a carrier scan."
            />
          ) : statusFilter ? (
            <EmptyState
              icon={<TruckIcon aria-hidden className="size-6" />}
              title={`Nothing is ${shipmentStatusLabel(statusFilter).toLowerCase()}`}
              message="Try another status, or All to see every shipment."
            />
          ) : (
            <EmptyState
              icon={<TruckIcon aria-hidden className="size-6" />}
              title="Nothing is shipping"
              message="Shipments to and from events show up here with live carrier tracking as they are booked."
            />
          )}
        </Panel>
      ) : (
        <>
          <DataTable guide="shipments-page-table"
            headers={[
              'Event',
              { label: 'Carrier & tracking', sortKey: 'carrier' },
              'To',
              { label: 'Status', sortKey: 'status' },
              { label: 'ETA', sortKey: 'eta' },
            ]}
            sort={sort}
            onSort={onSort}
            minWidth={820}
          >
            {shipments.data.shipments.map((shipment) => {
              const status = shipmentStatus(shipment);
              const event = relatedEvent(shipment);
              const eta = shipment.eta ? new Date(shipment.eta) : null;
              return (
                <tr key={shipment._id} className="border-t border-portal-line align-middle">
                  <td className="p-3 text-portal-small">
                    {event.id ? (
                      <Link
                        to={`/events/${event.id}?tab=shipments`}
                        className="text-portal-small font-bold text-brand hover:underline"
                      >
                        {event.label}
                      </Link>
                    ) : (
                      <span className="text-portal-small">{event.label}</span>
                    )}
                    {event.program ? (
                      <span className="block text-portal-micro text-portal-muted">
                        {event.program}
                      </span>
                    ) : null}
                  </td>
                  <td className="p-3 text-portal-small">
                    <span className="block text-portal-small">
                      {carrierLabel(shipment.carrier)}
                    </span>
                    <Link
                      to={`/shipments/${shipment._id}`}
                      className="block font-mono text-portal-micro text-brand hover:underline"
                    >
                      {shipment.trackingNumber || 'View shipment'}
                    </Link>
                    {returnLabelsOn && shipment.returnLabel ? (
                      <span className="mt-1 block">
                        <Pill tone="neutral">Return label</Pill>
                      </span>
                    ) : null}
                  </td>
                  <td className="p-3 text-portal-small">
                    {[shipment.addressTo?.name, shipment.addressTo?.city, shipment.addressTo?.state]
                      .filter(Boolean)
                      .join(', ') || '—'}
                  </td>
                  <td className="p-3 text-portal-small">
                    <Pill tone={SHIPMENT_TONES[status] ?? 'neutral'}>
                      {shipmentStatusLabel(status)}
                    </Pill>
                    {returnLabelsOn && shipment.returnLabelUnused ? (
                      <span className="ml-1.5">
                        <Pill tone="danger">Unused</Pill>
                      </span>
                    ) : null}
                  </td>
                  <td className="p-3 text-portal-small">
                    {eta ? eta.toLocaleDateString() : '—'}
                  </td>
                </tr>
              );
            })}
          </DataTable>
          <Pagination
            page={shipments.data.page}
            totalPages={shipments.data.totalPages}
            onChange={setPage}
            summary={`${rangeStart}–${rangeEnd} of ${shipments.data.total}`}
          />
        </>
      )}
    </section>
  );
}
